import { Deserializer } from 'jsonapi-serializer';
import { getFilterClause, createPartialURL } from './utilities/urlBuilder';
import { BuildingWrapper } from '../models/wrappers/BuildingWrapper';
import { createPagination } from "./utilities/paginationBuilder";
import { Building } from '../models/Building';
import { BuildingName } from "../models/BuildingName";
import { ListQuery } from '../models/ListQuery';
import { PaginationModel } from '../models/Pagination';
import { APIConnector } from './api/APIConnector';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { genericError } from '../models/GenericError';
import { NO_CONTENT } from '../constants/response-status-codes';
import { INTERNAL_SERVER_ERROR } from '../constants/response-status-codes';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';

const apiConnector = new APIConnector();

export const getBuildings = async (listParameters?: ListQuery): Promise<BuildingWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const fieldsBuidings = '&fields[building]=buildingName,totalFloors,totalLifts,isDefault,buildingDefault,site';
        const fieldsSite = '&fields[site]=siteName';
        const url = "building?include=site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + fieldsBuidings + fieldsSite;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const buildings: Building[] = data.map((building): Building => {
                    return {
                        id: building['id'],
                        name: building['building-name'],
                        floors: building['total-floors'],
                        lifts: building['total-lifts'],
                        site: building['site']['site-name'],
                        default: building['building-default'],
                        isDefault: building['is-default']
                    };
                });

                return resolve({ rows: buildings, pagination })
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBuildingById = async (id: string, listParameters?: ListQuery): Promise<Building> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const buildingFields = '&fields[building]=buildingName,totalFloors,totalLifts,buildingDefault,buildingDesc,site';
        const siteFields = '&fields[site]=siteName';
        filter = (filter === '' ? `&filter=id==${id}` : `${filter};id==${id}`);
        const url = "building?include=site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + buildingFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });;
                }

                const building: Building = {
                    id: data[0]['id'],
                    name: data[0]['building-name'],
                    floors: data[0]['total-floors'],
                    lifts: data[0]['total-lifts'],
                    site: data[0]['site']['site-name'],
                    siteId: data[0]['site']['id'],
                    description: data[0]['building-desc'] ?? '',
                    default: data[0]['building-default']
                };

                return resolve(building)
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBuildingNames = async (): Promise<BuildingName[]> => {
    try {
        let url = 'building?fields[building]=buildingName';
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const buildingName: BuildingName[] = data.map((building): BuildingName => {
                    return {
                        value: building['id'],
                        label: building['building-name'],
                        isDefault: building['is-default']
                    };
                });

                return resolve(buildingName);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBuildingNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: BuildingName[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = "building?include=site&fields[building]=buildingName,site,isDefault&fields[site]=siteName" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const buildingName: BuildingName[] = data.map((building): BuildingName => {
                    return {
                        value: building['id'],
                        label: building['building-name'],
                        optionalColumns: [{ key: 'site', value: building['site']['site-name'] }],
                        isDefault: building['is-default']
                    };
                });

                return resolve({ rows: buildingName, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeBuilding = async (building: Building, createBuilding = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'building',
                id: building.id,
                attributes: {
                    buildingDefault: building.default ?? 0,
                    buildingName: building.name,
                    buildingDesc: building.description
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: building.siteId
                        }
                    }
                }
            }
        }

        const url = `building${createBuilding ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createBuilding, newRecord).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createBuilding) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newBuilding) => {
                        if (error) {
                            return reject({ status: response.status, id: newBuilding.id, success: false, message: genericError });
                        }

                        return resolve({ id: newBuilding.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: building.id, success: true })
                }

                const json = await response.json();
                return reject({ status: response.status, id: building.id, success: false, message: getConstraintViolation(json.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: building.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteBuilding = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `building/${id}`;
        const building = {
            data: {
                type: "building",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, building);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
